import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import BlogCard from '../components/BlogCard';
import AnimatedPage from '../components/AnimatedPage';
import { Tag } from 'lucide-react';
import toast from 'react-hot-toast';

const Category = () => {
  const { category } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, [category]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'posts'),
        where('category', '==', category),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const postsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPosts(postsData);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedPage>
      <div className="min-h-screen pt-20">
        {/* Header */}
        <section className="py-20 px-4 bg-black text-white">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center mb-4">
                <Tag size={24} className="mr-2" />
                <span className="text-sm font-body font-medium uppercase tracking-wider">
                  Category
                </span>
              </div>
              <h1 className="heading-1 mb-4">
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </h1>
              <p className="body-text text-gray-300">
                {posts.length} {posts.length === 1 ? 'article' : 'articles'} found
              </p>
            </motion.div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="shimmer h-96 rounded-lg" />
                ))}
              </div>
            ) : posts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, index) => (
                  <BlogCard key={post.id} post={post} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-600 text-lg font-body">
                  No posts found in this category.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </AnimatedPage>
  );
};

export default Category;
